import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Globe, LayoutGrid, ListChecks } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { IconRenderer } from "../lib/icons";
import Header from "../components/Header";

export default function GalleryPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [installing, setInstalling] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/gallery");
      setItems(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const install = async (item) => {
    const pid = localStorage.getItem("rb_profile");
    setInstalling(item.id);
    try {
      await api.post(`/gallery/${item.id}/install`, { profile_id: pid });
      toast.success(`"${item.name}" added to your ${item.kind === "routine" ? "routines" : "boards"}`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setInstalling(null);
    }
  };

  return (
    <div data-testid="gallery-page" className="min-h-screen bg-background">
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-8 pb-24">
        <button data-testid="back-button" onClick={() => navigate("/app")} className="min-h-[48px] px-4 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
          <ArrowLeft size={20} strokeWidth={2.5} /> Back
        </button>

        <h1 className="mt-6 text-3xl font-extrabold tracking-tight flex items-center gap-3">
          <Globe size={32} strokeWidth={2.5} className="text-primary" /> Community gallery
        </h1>
        <p className="mt-2 text-lg text-muted-foreground">Routines and boards shared by other carers. One tap adds them to your account. Photos are never shared — icons only.</p>

        {items === null ? (
          <p className="mt-10 text-lg text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p data-testid="gallery-empty" className="mt-10 bg-white rounded-3xl border-2 border-dashed border-foreground/20 p-8 text-lg text-muted-foreground">
            Nothing shared yet — be the first! Publish one of your routines or boards from the dashboard.
          </p>
        ) : (
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {items.map((item) => (
              <div key={item.id} data-testid={`gallery-item-${item.id}`} className="bg-white rounded-3xl border-2 border-foreground/10 p-5">
                <div className="flex items-center gap-4">
                  <span className={`w-14 h-14 rounded-2xl grid place-items-center shrink-0 ${item.kind === "routine" ? "bg-primary/10" : "bg-[#9F7AEA]/10"}`}>
                    {item.kind === "routine"
                      ? <ListChecks size={28} strokeWidth={2.5} className="text-primary" />
                      : <LayoutGrid size={28} strokeWidth={2.5} className="text-[#9F7AEA]" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-extrabold truncate">{item.name}</h3>
                    <p className="text-sm font-semibold text-muted-foreground">
                      {item.kind === "routine" ? `${item.payload.steps.length}-step routine` : `${item.payload.tiles.length}-tile board`} · by {item.author_name} · {item.installs} installs
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(item.kind === "routine" ? item.payload.steps : item.payload.tiles).slice(0, 4).map((s, i) => (
                    <span key={i} className="px-3 py-1 rounded-full bg-secondary text-sm font-bold flex items-center gap-1">
                      <IconRenderer name={s.icon} size={14} /> {s.label}
                    </span>
                  ))}
                  {(item.kind === "routine" ? item.payload.steps : item.payload.tiles).length > 4 && (
                    <span className="px-3 py-1 text-sm font-bold text-muted-foreground">+ more</span>
                  )}
                </div>
                <button
                  data-testid={`gallery-install-${item.id}`}
                  onClick={() => install(item)}
                  disabled={installing === item.id}
                  className="mt-4 w-full min-h-[52px] rounded-full bg-primary text-white font-bold flex items-center justify-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-px disabled:opacity-60"
                >
                  <Download size={20} strokeWidth={2.5} /> {installing === item.id ? "Adding…" : "Add to my account"}
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
